import { useState, useEffect } from "react";
import api from "../lib/api";
import { CreditCard, Plus, Users, Crown, Pause, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const statusBadge = {
  active: { bg: "bg-[#E8F0E9]", text: "text-emerald-600" },
  paused: { bg: "bg-[#FDF3E1]", text: "text-amber-600" },
  cancelled: { bg: "bg-rose-50", text: "text-rose-600" },
  expired: { bg: "bg-[#FAFAFA]", text: "text-slate-400" },
};

export default function MembershipsPage() {
  const [plans, setPlans] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [showNewMembership, setShowNewMembership] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: "", price: 0, billing_period: "monthly", discount_percent: 0, description: "" });
  const [newMembership, setNewMembership] = useState({ client_id: "", plan_id: "", start_date: new Date().toISOString().split("T")[0] });

  useEffect(() => {
    const load = async () => {
      try {
        const [planRes, memRes, cliRes] = await Promise.all([
          api.get("/membership-plans"),
          api.get("/memberships"),
          api.get("/clients?limit=200"),
        ]);
        setPlans(planRes.data);
        setMemberships(memRes.data);
        setClients(cliRes.data.clients || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleCreatePlan = async () => {
    try {
      await api.post("/membership-plans", newPlan);
      toast.success("Plan created");
      setShowNewPlan(false);
      const res = await api.get("/membership-plans");
      setPlans(res.data);
    } catch (err) { toast.error("Failed to create plan"); }
  };

  const handleCreateMembership = async () => {
    try {
      await api.post("/memberships", newMembership);
      toast.success("Membership created");
      setShowNewMembership(false);
      const res = await api.get("/memberships");
      setMemberships(res.data);
    } catch (err) { toast.error("Failed to create membership"); }
  };

  const handleUpdateStatus = async (membershipId, status) => {
    try {
      await api.put(`/memberships/${membershipId}`, { status });
      toast.success(`Membership ${status}`);
      const res = await api.get("/memberships");
      setMemberships(res.data);
    } catch (err) { toast.error("Failed to update"); }
  };

  const totalMRR = memberships.filter(m => m.status === "active").reduce((s, m) => s + (m.plan_price || 0), 0);
  const activeCount = memberships.filter(m => m.status === "active").length;

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="memberships-page">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-light tracking-tight text-slate-900">Memberships</h1>
          <p className="text-slate-600 text-sm mt-1">Plans, packages, and recurring revenue</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="new-plan-btn" onClick={() => setShowNewPlan(true)} variant="outline" className="border-slate-200 rounded-full h-10 px-5 text-sm">
            <Plus className="w-4 h-4 mr-1.5" /> New Plan
          </Button>
          <Button data-testid="new-membership-btn" onClick={() => setShowNewMembership(true)} className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-10 px-5 text-sm">
            <Plus className="w-4 h-4 mr-1.5" /> Add Member
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Monthly Recurring</span></div>
          <p className="font-['Outfit'] text-3xl font-medium text-slate-900">${totalMRR}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Active Members</span></div>
          <p className="font-['Outfit'] text-3xl font-medium text-emerald-600">{activeCount}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-2"><Crown className="w-4 h-4 text-slate-400" strokeWidth={1.5} /><span className="text-xs font-bold uppercase tracking-widest text-slate-400">Plans Available</span></div>
          <p className="font-['Outfit'] text-3xl font-medium text-slate-900">{plans.length}</p>
        </div>
      </div>

      <Tabs defaultValue="members" className="space-y-4">
        <TabsList className="bg-[#FAFAFA] p-1 rounded-lg border border-slate-200">
          <TabsTrigger data-testid="tab-members" value="members" className="text-sm">Active Members</TabsTrigger>
          <TabsTrigger data-testid="tab-plans" value="plans" className="text-sm">Plans</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Plan</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Since</th>
                  <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-600">Price</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {memberships.map(m => {
                  const badge = statusBadge[m.status] || statusBadge.active;
                  return (
                    <tr key={m.membership_id} className="border-b border-slate-200 hover:bg-[#FAFAFA] transition-colors" data-testid={`membership-${m.membership_id}`}>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">{m.client_name}</td>
                      <td className="px-4 py-4 text-sm text-slate-600">{m.plan_name}</td>
                      <td className="px-4 py-4"><span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>{m.status}</span></td>
                      <td className="px-4 py-4 text-sm text-slate-400">{m.start_date}</td>
                      <td className="px-4 py-4 text-sm text-right font-medium text-slate-900">${m.plan_price}/mo</td>
                      <td className="px-4 py-4">
                        <div className="flex gap-1 justify-end">
                          {m.status === "active" && (
                            <button onClick={() => handleUpdateStatus(m.membership_id, "paused")} className="p-1.5 rounded-md hover:bg-[#FDF3E1] text-amber-600" title="Pause">
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {m.status === "paused" && (
                            <button onClick={() => handleUpdateStatus(m.membership_id, "active")} className="p-1.5 rounded-md hover:bg-[#E8F0E9] text-emerald-600" title="Reactivate">
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="plans">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map(plan => (
              <div key={plan.plan_id} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`plan-${plan.plan_id}`}>
                <h3 className="font-['Outfit'] text-lg font-medium text-slate-900 mb-1">{plan.name}</h3>
                <p className="text-sm text-slate-600 mb-4">{plan.description}</p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="font-['Outfit'] text-3xl font-medium text-slate-900">${plan.price}</span>
                  <span className="text-sm text-slate-400">/{plan.billing_period}</span>
                </div>
                {plan.discount_percent > 0 && (
                  <div className="text-xs text-emerald-600 bg-[#E8F0E9] px-2.5 py-1 rounded-full inline-flex mb-3">
                    {plan.discount_percent}% off all services
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* New Plan Modal */}
      <Dialog open={showNewPlan} onOpenChange={setShowNewPlan}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-2xl p-8">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">New Membership Plan</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Input data-testid="plan-name" value={newPlan.name} onChange={e => setNewPlan(p => ({...p, name: e.target.value}))} placeholder="Plan name" className="h-11 border-slate-200 rounded-lg" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="plan-price" type="number" value={newPlan.price} onChange={e => setNewPlan(p => ({...p, price: Number(e.target.value)}))} placeholder="Price" className="h-11 border-slate-200 rounded-lg" />
              <Input data-testid="plan-discount" type="number" value={newPlan.discount_percent} onChange={e => setNewPlan(p => ({...p, discount_percent: Number(e.target.value)}))} placeholder="Discount %" className="h-11 border-slate-200 rounded-lg" />
            </div>
            <Input value={newPlan.description} onChange={e => setNewPlan(p => ({...p, description: e.target.value}))} placeholder="Description" className="h-11 border-slate-200 rounded-lg" />
            <Button data-testid="create-plan-btn" onClick={handleCreatePlan} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Create Plan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Membership Modal */}
      <Dialog open={showNewMembership} onOpenChange={setShowNewMembership}>
        <DialogContent className="sm:max-w-[440px] bg-white border-slate-200 rounded-2xl p-8">
          <DialogHeader><DialogTitle className="font-['Outfit'] text-xl font-medium">Add Member</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={newMembership.client_id} onValueChange={v => setNewMembership(p => ({...p, client_id: v}))}>
              <SelectTrigger data-testid="member-client" className="h-11 border-slate-200 rounded-lg"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={newMembership.plan_id} onValueChange={v => setNewMembership(p => ({...p, plan_id: v}))}>
              <SelectTrigger data-testid="member-plan" className="h-11 border-slate-200 rounded-lg"><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>{plans.map(p => <SelectItem key={p.plan_id} value={p.plan_id}>{p.name} - ${p.price}/mo</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="member-start" type="date" value={newMembership.start_date} onChange={e => setNewMembership(p => ({...p, start_date: e.target.value}))} className="h-11 border-slate-200 rounded-lg" />
            <Button data-testid="create-membership-btn" onClick={handleCreateMembership} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">Add Member</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
